/** Small presentation helpers shared across features. */
import { type ClassValue, clsx } from "clsx";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { twMerge } from "tailwind-merge";

/** Merge Tailwind class names, resolving conflicts (shadcn convention). */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Format an ISO timestamp as a short local date-time, or a dash when empty. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  return format(parseISO(value), "yyyy-MM-dd HH:mm");
}

/** Format an ISO date (or timestamp) as a local date, or a dash when empty. */
export function formatDate(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  return format(parseISO(value), "yyyy-MM-dd");
}

/** Relative time such as "3 hours ago", or a dash when empty. */
export function formatRelative(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  return formatDistanceToNow(parseISO(value), { addSuffix: true });
}

/** Format a `Money` value (`{amount, currency}`) with the current locale. */
export function formatMoney(
  money: { amount: string; currency: string } | null | undefined,
  locale?: string,
): string {
  if (!money) {
    return "—";
  }
  const amount = Number(money.amount);
  if (Number.isNaN(amount)) {
    return `${money.amount} ${money.currency}`;
  }
  return new Intl.NumberFormat(locale, { style: "currency", currency: money.currency }).format(
    amount,
  );
}

/** Format a decimal string trimmed of trailing zeros (e.g. "12.500" → "12.5"). */
export function formatQuantity(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const n = Number(value);
  return Number.isNaN(n)
    ? String(value)
    : n.toLocaleString(undefined, { maximumFractionDigits: 3 });
}

/** Format a percentage with one decimal, or a dash when null. */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return "—";
  }
  return `${value.toFixed(1)}%`;
}

/** Render a coordinate pair as "lat, lon" with 4 decimals. */
export function formatPoint(point: { lat: number; lon: number } | null | undefined): string {
  if (!point) {
    return "—";
  }
  return `${point.lat.toFixed(4)}, ${point.lon.toFixed(4)}`;
}
